import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'

const URL_PREFIX = 'services/plugin-quality-monitoring/quality_monitoring'
export const API_ROUTES = {
    GET_FACTORY_TREE: URL_PREFIX + '/assetTree',
    COUNTRY: URL_PREFIX + '/country',
    FACTORY: URL_PREFIX + '/country/factory',
    LINE: URL_PREFIX + '/country/factory/line',
    LINES: URL_PREFIX + '/country/factory/lines',
    GET_PRODUCT_TREE: URL_PREFIX + '/productTree',
    PRODUCT: URL_PREFIX + '/product',
    SKU: URL_PREFIX + '/product/sku',
    SKUS: URL_PREFIX + '/products/skus',
    ANALYSIS_RESULT: URL_PREFIX + '/analysisResult',
    EDIT_ANALYSIS: URL_PREFIX + '/analysisResult/actionPlan',
    DELAYED_JUSTIFICATION: URL_PREFIX + '/delayJustification',
    DELETE_PRODUCT_IMAGE_ID: URL_PREFIX + '/productImageId'
}

export function getProduct(productName: string): Promise<ProductData> {
    return fetch(`${URL_PREFIX}/${productName}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(resp => resp.json())
}

export function getAssetTree(assetType: AssetType): Promise<CountryData[] | ProductData[]> {
    const url = API_ROUTES[`GET_${assetType.toUpperCase()}_TREE`]

    return fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(resp => resp.json())
        .catch(() => globalNotification.error(i18n('Error retrieving asset tree')))
}

/**
 * Use this function to edit the names of the node in the asset tree
 *
 * It only updates one name, so you need to call it for each node being altered
 *
 * It behaves differently depending on the number of arguments
 *
 * Ex: If it receives 4 args, it will update a line name
 *
 * Ex: If it receives 3 args, it will update a factory name
 *
 * Ex: If it receives 2 args, it will update a country name
 *
 * Start from inside out. First update a line, than a factory and lastly the country
 *
 * @param oldName name being replaced
 * @param newName element new name
 * @param country name of the country node that holds this element
 * @param factory name of the factory node that holds this element
 *
 */
export function editCountryTreeElement(
    oldName: string,
    newName: string,
    elementType: 'country' | 'factory',
    relatedTreeElement: string
): Promise<Response> {
    let url = API_ROUTES.COUNTRY
    let params
    params = { oldName, newName, factory: relatedTreeElement }
    if (elementType === 'factory') {
        url = API_ROUTES.FACTORY
        params = { oldName, newName, country: relatedTreeElement }
    }

    return fetch(url, {
        method: 'PUT',
        body: JSON.stringify(params),
        headers: {
            'Content-Type': 'application/json'
        }
    }).catch(() => globalNotification.error(i18n('Error editing configurations')))
}

export function editProductTreeElement(params: EditProductTreeParams): Promise<Response> {
    let url = API_ROUTES.PRODUCT
    if (params.contentType === 'sku') url += '/sku'

    return fetch(url, {
        method: 'PUT',
        body: JSON.stringify(params),
        headers: {
            'Content-Type': 'application/json'
        }
    }).catch(() => globalNotification.error(i18n('Error editing configurations')))
}

export function deleteAsset(params: {
    country?: string
    factory?: string
    line?: string
    productName?: string
    skuName?: string
}): Promise<Response> {
    let url
    if (params.skuName) url = API_ROUTES.SKU
    else if (params.productName) url = API_ROUTES.PRODUCT
    else if (params.line) url = API_ROUTES.LINE
    else if (params.factory) url = API_ROUTES.FACTORY

    return fetch(url, {
        method: 'DELETE',
        body: JSON.stringify(params),
        headers: {
            'Content-Type': 'application/json'
        }
    })
}

export function postAsset(
    assetType: 'factory' | 'product' | 'sku' | 'skus' | 'lines' | 'line',
    params: SkuParam | ProductParam | FactoryParam | SkuParam[] | LineParam[] | LineParam
): Promise<Response> {
    const url = API_ROUTES[assetType.toUpperCase()]

    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
    }).catch(() => globalNotification.error(i18n('Error making request')))
}
export function postOrEditLine(params: FactoryParam & { line: LineData }, type: 'add' | 'edit'): Promise<Response> {
    const url = type === 'add' ? API_ROUTES.LINE : API_ROUTES.LINE + '/edit'

    return fetch(url, {
        method: type === 'add' ? 'post' : 'put',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
    })
}

export function getAllAnalysisResults(): Promise<[]> {
    return fetch(API_ROUTES.ANALYSIS_RESULT, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(resp => resp.json())
}

export function getAnalysisResults(urlParams: string[], selectedSpan: string): Promise<AnalysisEvent[]> {
    const selectedAssets = urlParams.filter(param => param !== 'overview' && param !== 'sensorial').join('/')
    const url = `${API_ROUTES.ANALYSIS_RESULT}/${selectedAssets}?span=${selectedSpan}`

    return fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(resp => resp.json())
}

export function postAnalysis(params: AnalysisEvent | StoppedLineEvent): Promise<Response> {
    return fetch(API_ROUTES.ANALYSIS_RESULT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
    })
}
export function editAnalysis(params: AnalysisEvent): Promise<Response> {
    return fetch(API_ROUTES.EDIT_ANALYSIS, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
    })
}

export function getDelayJustification(
    params: LineParam
): Promise<{
    needsJustification: boolean
    justification: StoppedLineEvent[]
    mostRecentEvent: AnalysisEvent[] | StoppedLineEvent[]
}> {
    const { country, factory, line } = params
    const url = API_ROUTES.DELAYED_JUSTIFICATION + `?country=${country}&factory=${factory}&line=${line}`

    return fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(resp => resp.json())
}

export function deleteProductImageId(productName: string): Promise<void | Response> {
    return fetch(API_ROUTES.DELETE_PRODUCT_IMAGE_ID, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ productName: productName })
    }).catch(err => {
        console.error('get error', err)
    })
}
export function deleteFromPluginUploads(imageId: string): void {
    fetch(`/services/plugin-upload/delete/${imageId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
        credentials: 'include'
    })
        .then(response => {
            if (response.ok) {
                console.log('file deleted')
            }
        })
        .catch(err => {
            console.error('get error', err)
        })
}

export function postImageInPluginUpload(imagePostBody: FormData): Promise<void | (Response & { uuid: string })> {
    return fetch('/services/plugin-upload/upload', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'include',
        body: imagePostBody
    })
        .then(resp => resp.json())
        .catch(err => {
            console.error('upload error', err)
        })
}

export function getImageName(imageId: string): Promise<string> {
    return fetch(`/services/plugin-upload/info/${imageId}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include'
    })
        .then(resp => resp.json())
        .then(response => {
            return response.name
        })
        .catch(err => {
            console.error('get error', err)
        })
}
